import { useAuthContext } from '@/context'
import { useCompleteOnboarding } from '@/services/business/mutations'
import { useQueryOnboardingStatus } from '@/services/business/queries'

import { useCallback, useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { defaultSteps } from '../components/defaultSteps'
import { mobileConsumerSteps } from '../components/mobileConsumerSteps'
import { OnboardingResponses } from '../types'

export const useOnboarding = () => {
    const { activeBusiness, user } = useAuthContext()
    const [showOnboarding, setShowOnboarding] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)

    const isMobileConsumer = user?.experience_variant === 'mobile_consumer'
    const onboardingSteps = useMemo(
        () => (isMobileConsumer ? mobileConsumerSteps : defaultSteps),
        [isMobileConsumer]
    )

    const {
        data: onboardingData,
        isLoading,
        refetch,
    } = useQueryOnboardingStatus({
        enabled: !!activeBusiness,
    })

    const completeOnboardingMutation = useCompleteOnboarding({
        onSuccess: () => {
            toast.success(isMobileConsumer ? 'You’re all set.' : 'Onboarding completed successfully!')
            setShowOnboarding(false)
            setIsSubmitting(false)
            refetch()
        },
        onError: (error: any) => {
            console.error('Onboarding error:', error)
            toast.error(error?.response?.data?.message || 'Failed to complete onboarding. Please try again.')
            setIsSubmitting(false)
        },
    })

    useEffect(() => {
        if (!isLoading && onboardingData && activeBusiness) {
            const completed = !!onboardingData.completedAt
            setShowOnboarding(!completed)
        }
    }, [onboardingData, isLoading, activeBusiness])

    const submitOnboarding = useCallback(
        (responses: OnboardingResponses) => {
            setIsSubmitting(true)

            const transformedData: Record<string, unknown> = {}

            Object.entries(responses).forEach(([key, value]) => {
                if (key === 'invite-team') {
                    return
                }
                if (Array.isArray(value)) {
                    transformedData[key] = value.length === 1 ? value[0] : value
                } else {
                    transformedData[key] = value
                }
            })

            if (isMobileConsumer) {
                transformedData.experience = 'mobile_consumer'
            }

            completeOnboardingMutation.mutate({ data: transformedData })
        },
        [completeOnboardingMutation, isMobileConsumer]
    )

    const handleComplete = (responses: OnboardingResponses) => {
        submitOnboarding(responses)
    }

    const handleSkipAll = useCallback(() => {
        submitOnboarding({
            skipped: 'true',
            experience: isMobileConsumer ? 'mobile_consumer' : 'full_saas',
            'use-case': isMobileConsumer ? ['exploring'] : ['not-sure'],
        })
    }, [isMobileConsumer, submitOnboarding])

    const handleClose = () => {
        setShowOnboarding(false)
    }

    return {
        showOnboarding,
        isSubmitting,
        isLoading,
        isMobileConsumer,
        onboardingSteps,
        userName: user?.fullname,
        onComplete: handleComplete,
        onClose: handleClose,
        onSkipAll: handleSkipAll,
        onboardingStatus: onboardingData?.data,
    }
}
